// Global Instructions Rule Applied!
//
// Which funds a picker offers. Pure — no React, no Redux, no fetch — so the
// rules are testable on their own (fundOptions.test.ts).
//
// WHY THE GATING IS HERE AND NOT IN SQL. globalConfigs.funds is not just picker
// fuel: it is also the fund LABEL resolver for historical records
// (getFundNameById), the currency resolver on the Terms tab, the
// pipeline-origination source for prospect stage defaults, and the option
// source for four grid filter dropdowns. Filtering the payload would blank
// fund names on old records and make closed funds unfilterable in the grids.
// So the whole list keeps flowing and the gating happens here, per call site.
//
// TWO RULES, NOT ONE (owner, 2026-09-04):
//
//   scope 'fundraising' — the fund's fundraising clock is open. For pickers
//       that choose a fund to raise INTO: the prospect forms, bulk add, the
//       fundraising kanban.
//   scope 'any' (default) — the fund is not terminal on EITHER clock.
//       Deliberately looser, and the difference matters: you still book
//       commitments, capital calls and transactions against a fund whose raise
//       closed years ago. Only Cancelled and Terminated drop out.
//
// EVERYTHING FAILS OPEN. A fund with no status — every fund on the day the
// columns shipped, and any the admin never classified — is active and open.
// The gate may never be the reason a fund silently disappears from a picker.
// The same rule holds when the catalogue itself is missing (an older backend):
// no statuses means nothing is terminal, so everything shows.

import type { FundsModel } from '../../state_manager/data_models/funds_model.ts'
import type { FundStatusModel } from '../../state_manager/data_models/fund_status_model.ts'

export type FundScope = 'any' | 'fundraising'

export interface FundOptionRow {
  value: string
  label: string
  /** False only when the gate would have hidden it — a pinned or shown-all row. */
  isActive: boolean
  row: FundsModel
}

export interface FundOptionsGate {
  scope?: FundScope
  /**
   * Ids on the record being edited. ALWAYS present in the list, whatever their
   * status. Without this, opening an old capital call whose fund has since
   * been cancelled silently blanks the field on save.
   */
  pin?: string | string[] | null
  /** Hard opt-out for accounting / reporting / historical surfaces. */
  includeAll?: boolean
  /** Mirror the data RPCs' managed_by_us scoping when a caller needs it. */
  onlyManagedByUs?: boolean
}

const statusById = (statuses: FundStatusModel[] | undefined): Map<string, FundStatusModel> =>
  new Map((statuses || []).map((s) => [s.id, s]))

/**
 * Is this fund offered by default under `scope`?
 *
 * Reads the FLAGS, never the name or the value — an admin renaming "Closed"
 * must not change who is followed or what a picker shows. Prefers the two
 * booleans the RPC already computed (is_active / is_fundraising_open) and
 * falls back to resolving the catalogue itself, so the function still answers
 * correctly for a fund row assembled in a test.
 */
export function isFundActive(
  fund: FundsModel,
  byId: Map<string, FundStatusModel>,
  scope: FundScope = 'any'
): boolean {
  if (scope === 'fundraising') {
    if (typeof fund.is_fundraising_open === 'boolean') return fund.is_fundraising_open
    const fr = fund.fundraising_status_id ? byId.get(fund.fundraising_status_id) : undefined
    return fr ? fr.is_open : true // no status, or a status we cannot see => open
  }
  if (typeof fund.is_active === 'boolean') return fund.is_active
  const fr = fund.fundraising_status_id ? byId.get(fund.fundraising_status_id) : undefined
  const inv = fund.investment_status_id ? byId.get(fund.investment_status_id) : undefined
  return !(fr?.is_terminal ?? false) && !(inv?.is_terminal ?? false)
}

const labelOf = (f: FundsModel): string => f.name || f.legal_name || f.id

/**
 * Build the option list.
 *
 * Guarantees, in order:
 *   1. every pinned id is present EXACTLY once, even when inactive;
 *   2. otherwise the active funds (or all of them, when showAll/includeAll);
 *   3. sorted by label, with pins interleaved rather than appended — the fund
 *      already on the record must not have to be hunted for at the bottom.
 */
export function selectFundOptions(
  funds: FundsModel[],
  statuses: FundStatusModel[],
  gate: FundOptionsGate & { showAll?: boolean } = {}
): { options: FundOptionRow[]; hiddenCount: number } {
  const { scope = 'any', pin, includeAll = false, onlyManagedByUs = false, showAll = false } = gate
  const byId = statusById(statuses)
  const pinned = new Set(
    (Array.isArray(pin) ? pin : pin ? [pin] : []).filter(Boolean) as string[]
  )

  const inScope = (funds || []).filter((f) => !onlyManagedByUs || f.managed_by_us !== false)

  const rows: FundOptionRow[] = []
  let hiddenCount = 0

  for (const f of inScope) {
    const active = isFundActive(f, byId, scope)
    // A pinned fund is never hidden and never duplicated: the Set membership
    // is what admits it, and each fund is visited once.
    if (active || includeAll || showAll || pinned.has(f.id)) {
      rows.push({ value: f.id, label: labelOf(f), isActive: active, row: f })
    } else {
      hiddenCount += 1
    }
  }

  rows.sort((a, b) => a.label.localeCompare(b.label))
  return { options: rows, hiddenCount }
}
